import { useEffect, useState } from 'react';

import { timeToMinutes } from './timeUtils';

import { validateSlotClient } from '../../utils/slottimeutils';
import { toast } from 'react-toastify';
import { dateAva } from '../../api/bookingAPI';

const useSlotManager = ({ data, openTime, closeTime, selectedDate, type }) => {
    // ========================================================
    // EXISTING TEMPLATE SLOTS
    // ========================================================

    const [displaySlots, setDisplaySlots] = useState([]);
    const [existingDateSlots, setExistingDateSlots] = useState([]);
    const [slotExists, setSlotExists] = useState(false);
    const [selectedTemplateKeys, setSelectedTemplateKeys] = useState(new Set());

    // ========================================================
    // CUSTOM SLOT
    // ========================================================

    const [customStart, setCustomStart] = useState('');

    const [customEnd, setCustomEnd] = useState('');

    const [customTotal, setCustomTotal] = useState('');

    // ========================================================
    // ADDED SLOTS
    // ========================================================

    const [addedSlots, setAddedSlots] = useState([]);

    // Which added slot is currently being edited
    const [editingSlotIndex, setEditingSlotIndex] = useState(null);

    // ========================================================
    // ERROR
    // ========================================================

    const [slotError, setSlotError] = useState('');

    // ========================================================
    // LOAD ACTIVITY DATA
    // ========================================================

    useEffect(() => {
        if (data?.timeSlots?.length) {
            setDisplaySlots(data.timeSlots);
        } else {
            setDisplaySlots([]);
        }
    }, [data?.timeSlots]);

    useEffect(() => {
        if (!selectedDate || !data?._id) {
            return;
        }

        fetchSlotsForDate();
    }, [selectedDate, data?._id]);

    const fetchSlotsForDate = async () => {
        try {
            if (!selectedDate || !data?._id) {
                return;
            }

            const response = await dateAva(
                {
                    date: selectedDate.format('YYYY-MM-DD'),
                    contentId: data._id
                },
                type
            );

            const oldSlots = response?.data?.data?.slots || [];

            const defaultSlots = data?.timeSlots || [];

            const hasExistingSlots = oldSlots.length > 0;

            setExistingDateSlots(oldSlots);
            setSlotExists(hasExistingSlots);

            if (hasExistingSlots) {
                // Date already has slots
                // Show only those slots
                setDisplaySlots(oldSlots);
            } else {
                // New date
                // Show activity default slots
                setDisplaySlots(defaultSlots);
            }
        } catch (error) {
            console.error('Failed to fetch slots for date:', error);

            setExistingDateSlots([]);
            setSlotExists(false);

            // API failed → fallback to default slots
            setDisplaySlots(data?.timeSlots || []);
        }
    };
    // ========================================================
    // TEMPLATE KEY
    // ========================================================

    const templateKey = (slot) => {
        return `${slot.startTime}-${slot.endTime}`;
    };

    // ========================================================
    // CLEAR CUSTOM FORM
    // ========================================================

    const clearCustomSlotForm = () => {
        setCustomStart('');
        setCustomEnd('');
        setCustomTotal('');

        setEditingSlotIndex(null);

        setSlotError('');
    };

    // ========================================================
    // VALIDATE TIME ORDER
    // ========================================================

    const validateTimeOrder = (startTime, endTime) => {
        const start = timeToMinutes(startTime);

        const end = timeToMinutes(endTime);

        if (start === null || end === null) {
            return 'Please select valid start and end times.';
        }

        if (end <= start) {
            return 'End time must be greater than start time.';
        }

        return '';
    };

    // ========================================================
    // TOGGLE EXISTING TEMPLATE SLOT
    // ========================================================

    const handleToggleTemplate = (template) => {
        setSlotError('');

        const key = templateKey(template);

        // ----------------------------------------------------
        // REMOVE EXISTING TEMPLATE SLOT
        // ----------------------------------------------------

        if (selectedTemplateKeys.has(key)) {
            setAddedSlots((prev) => prev.filter((slot) => templateKey(slot) !== key));

            setSelectedTemplateKeys((prev) => {
                const next = new Set(prev);

                next.delete(key);

                return next;
            });

            return;
        }

        // ----------------------------------------------------
        // VALIDATE NEW TEMPLATE SLOT
        // ----------------------------------------------------

        const error = validateSlotClient({
            startTime: template.startTime,

            endTime: template.endTime,

            openTime,

            closeTime,

            existingSlots: addedSlots
        });

        if (error) {
            toast.error(error);
            // setSlotError(error);

            return;
        }

        // ----------------------------------------------------
        // ADD TEMPLATE SLOT
        // ----------------------------------------------------

        setAddedSlots((prev) => [
            ...prev,

            {
                startTime: template.startTime,

                endTime: template.endTime,

                totalSlots: template.totalSlots || template.maxSlots || 1,

                source: 'template'
            }
        ]);

        setSelectedTemplateKeys((prev) => new Set([...prev, key]));
    };

    // ========================================================
    // CHANGE START TIME
    // ========================================================

    const handleStartTimeChange = (value) => {
        setCustomStart(value);

        if (!customEnd) {
            return;
        }

        const start = timeToMinutes(value);

        const end = timeToMinutes(customEnd);

        // Existing End Time is no longer
        // valid after changing Start Time
        if (start !== null && end !== null && end <= start) {
            setCustomEnd('');
        }
    };

    // ========================================================
    // ADD / UPDATE CUSTOM SLOT
    // ========================================================

    const handleAddCustomSlot = () => {
        setSlotError('');

        // ----------------------------------------------------
        // START TIME
        // ----------------------------------------------------

        if (!customStart) {
            toast.error('Please select a start time.');
            setSlotError('Please select a start time.');

            return false;
        }

        // ----------------------------------------------------
        // END TIME
        // ----------------------------------------------------

        if (!customEnd) {
            toast.error('Please select an end time.');
            setSlotError('Please select an end time.');

            return false;
        }

        // ----------------------------------------------------
        // TIME ORDER
        // ----------------------------------------------------

        const timeError = validateTimeOrder(customStart, customEnd);

        if (timeError) {
            toast.error(timeError);
            setSlotError(timeError);

            return false;
        }

        // ----------------------------------------------------
        // TOTAL SLOTS
        // ----------------------------------------------------

        if (!customTotal || Number(customTotal) < 1) {
            toast.error('Enter a valid number of total slots.');
            setSlotError('Enter a valid number of total slots.');

            return false;
        }

        // ----------------------------------------------------
        // EXISTING SLOTS TO CHECK
        //
        // If editing, exclude the slot being edited.
        // ----------------------------------------------------

        const newSlotsToValidate =
            editingSlotIndex !== null ? addedSlots.filter((_, index) => index !== editingSlotIndex) : addedSlots;

        const slotsToValidate = [...existingDateSlots, ...newSlotsToValidate];

        // ----------------------------------------------------
        // OPERATING HOURS + DUPLICATE VALIDATION
        // ----------------------------------------------------

        const error = validateSlotClient({
            startTime: customStart,

            endTime: customEnd,

            openTime,

            closeTime,

            existingSlots: slotsToValidate
        });

        if (error) {
            toast.error(error);
            // setSlotError(error);

            return false;
        }

        // ====================================================
        // EDIT EXISTING SLOT
        // ====================================================

        if (editingSlotIndex !== null) {
            setAddedSlots((prev) =>
                prev.map((slot, index) => {
                    if (index !== editingSlotIndex) {
                        return slot;
                    }

                    return {
                        ...slot,

                        startTime: customStart,

                        endTime: customEnd,

                        totalSlots: Number(customTotal)
                    };
                })
            );
        }

        // ====================================================
        // ADD NEW CUSTOM SLOT
        // ====================================================
        else {
            setAddedSlots((prev) => [
                ...prev,

                {
                    startTime: customStart,

                    endTime: customEnd,

                    totalSlots: Number(customTotal),

                    source: 'custom'
                }
            ]);
        }

        // ----------------------------------------------------
        // RESET FORM
        // ----------------------------------------------------

        clearCustomSlotForm();

        return true;
    };

    // ========================================================
    // EDIT ADDED SLOT
    // ========================================================

    const handleEditSlot = (index) => {
        const slot = addedSlots[index];

        if (!slot) {
            return;
        }

        setCustomStart(slot.startTime);

        setCustomEnd(slot.endTime);

        setCustomTotal(slot.totalSlots);

        setEditingSlotIndex(index);

        setSlotError('');
    };

    // ========================================================
    // REMOVE ADDED SLOT
    // ========================================================

    const handleRemoveSlot = (index) => {
        const removed = addedSlots[index];

        if (!removed) {
            return;
        }

        // ----------------------------------------------------
        // Remove from added slots
        // ----------------------------------------------------

        setAddedSlots((prev) => prev.filter((_, i) => i !== index));

        // ----------------------------------------------------
        // If template slot was removed,
        // unselect its template card.
        // ----------------------------------------------------

        if (removed.source === 'template') {
            const key = templateKey(removed);

            setSelectedTemplateKeys((prev) => {
                const next = new Set(prev);

                next.delete(key);

                return next;
            });
        }

        // ----------------------------------------------------
        // If currently editing this slot,
        // cancel editing.
        // ----------------------------------------------------

        if (editingSlotIndex === index) {
            clearCustomSlotForm();
        }

        // ----------------------------------------------------
        // If editing a slot after the
        // removed index, adjust index.
        // ----------------------------------------------------
        else if (editingSlotIndex !== null && editingSlotIndex > index) {
            setEditingSlotIndex((prev) => prev - 1);
        }
    };

    // ========================================================
    // RETURN
    // ========================================================

    return {
        // Existing templates
        displaySlots,
        slotExists,
        selectedTemplateKeys,
        setSelectedTemplateKeys,
        existingDateSlots,
        // Custom form
        customStart,
        customEnd,
        customTotal,

        setCustomStart,
        setCustomEnd,
        setCustomTotal,

        handleStartTimeChange,

        // Added slots
        addedSlots,
        setAddedSlots,
        setExistingDateSlots,
        editingSlotIndex,

        // Error
        slotError,

        // Handlers
        handleToggleTemplate,
        handleAddCustomSlot,
        handleEditSlot,
        handleRemoveSlot,
        clearCustomSlotForm
    };
};

export default useSlotManager;
